import type { ReadingSlide } from "../ui/StartReader";
import { C, Frame, MONO } from "../ui/readingArt";

/**
 * "Read while you wait" summary for "The Visitor" start screen. Three short
 * slides (why a human is the control, what to watch for, what to do) shown in
 * the reader while the 3D room downloads. Content only; the reader UI lives in
 * ui/StartReader.tsx. Animation classes (sr-anim-*) are defined in
 * ui/startReader.css. Where an element needs both a position and an animation,
 * the position sits on an outer <g> and the class on an inner one, because the
 * CSS animation transform would replace a transform attribute on the same node.
 */

/* ------------------------------------------------------------------ */
/* 1. the entrance: a visitor with a lanyard at the buzzer, two moves   */
/* ------------------------------------------------------------------ */
function EntranceArt(): JSX.Element {
  const H = 288;
  return (
    <Frame label="Example: the main entrance" aria="A visitor with a lanyard and a laptop bag buzzes at the office door and says he is from HQ IT. Below, two choices: open the door, or verify first." height={H}>
      <text x="16" y="53" fontSize="14" fontWeight="700" fill={C.text}>
        Main entrance
      </text>
      <rect x="282" y="38" width="62" height="20" rx="10" fill={C.amber} fillOpacity="0.12" stroke={C.amber} strokeOpacity="0.45" />
      <circle className="sr-anim-blink" cx="294" cy="48" r="3" fill={C.amber} />
      <text x="302" y="52" fontSize="10.5" fontWeight="700" fill={C.amber}>
        Buzzer
      </text>
      <line x1="16" y1="70.5" x2="344" y2="70.5" stroke={C.line} />

      {/* scene */}
      <rect x="16.5" y="80.5" width="327" height="108" rx="8" fill={C.panel2} stroke={C.line} />

      {/* door and intercom */}
      <rect x="276" y="92" width="52" height="96" rx="3" fill={C.code} stroke={C.line} />
      <circle cx="286" cy="142" r="2.5" fill={C.muted} />
      <rect x="246" y="118" width="20" height="34" rx="4" fill={C.code} stroke={C.line} />
      <circle className="sr-anim-blink" cx="256" cy="127" r="2.5" fill={C.amber} />
      <line x1="251" y1="136" x2="261" y2="136" stroke={C.muted} strokeLinecap="round" />
      <line x1="251" y1="140" x2="261" y2="140" stroke={C.muted} strokeLinecap="round" />
      <circle cx="256" cy="147" r="2.4" fill="none" stroke={C.muted} />

      {/* visitor */}
      <circle cx="62" cy="112" r="11" fill={C.muted} />
      <path d="M40 188 V140 a14 14 0 0 1 14 -14 H70 a14 14 0 0 1 14 14 V188 Z" fill={C.panel} stroke={C.line} />
      <polyline points="55,127 62,146 69,127" fill="none" stroke={C.amber} strokeWidth="1.5" strokeLinejoin="round" />
      <rect x="55" y="146" width="14" height="18" rx="2" fill={C.amber} />
      <text x="62" y="158" fontSize="8" fontWeight="700" fill={C.code} textAnchor="middle">
        IT
      </text>
      <circle className="sr-anim-ring" cx="62" cy="155" r="10" fill="none" stroke={C.amber} strokeWidth="1.5" />
      <path d="M99 160 v-5 h16 v5" fill="none" stroke={C.muted} strokeWidth="1.5" strokeLinejoin="round" />
      <rect x="92" y="160" width="30" height="24" rx="4" fill={C.code} stroke={C.line} />

      {/* what he says */}
      <rect x="128.5" y="92.5" width="108" height="46" rx="8" fill={C.panel} stroke={C.line} />
      <polygon points="129,110 120,118 129,124" fill={C.panel} />
      <polyline points="129,110 120,118 129,124" fill="none" stroke={C.line} strokeLinejoin="round" />
      <line x1="129" y1="111" x2="129" y2="123" stroke={C.panel} strokeWidth="2" />
      <text x="138" y="112" fontSize="10.5" fontWeight="700" fill={C.text}>
        Marco from HQ IT.
      </text>
      <text x="138" y="127" fontSize="10.5" fill={C.soft}>
        Can you let me in?
      </text>

      <text x="16" y="208" fontSize="11" fill={C.soft}>
        Lanyard, laptop bag, in a hurry. None of it is proof.
      </text>

      <text x="16" y="236" fontSize="10.5" fill={C.muted}>
        You are the control. Pick one:
      </text>
      <rect x="16.5" y="244.5" width="158" height="30" rx="8" fill={C.panel2} stroke={C.red} strokeOpacity="0.55" />
      <text x="95.5" y="263" fontSize="12" fontWeight="700" fill={C.red} textAnchor="middle">
        Open the door
      </text>
      <rect className="sr-anim-pulse" x="185.5" y="244.5" width="158" height="30" rx="8" fill={C.mint} fillOpacity="0.14" stroke={C.mint} strokeOpacity="0.55" />
      <text x="264.5" y="263" fontSize="12" fontWeight="700" fill={C.mint} textAnchor="middle">
        Verify first
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 2. the pretext lines a visitor uses, and the red-flag chips          */
/* ------------------------------------------------------------------ */
function PretextArt(): JSX.Element {
  const H = 292;
  const lines = [
    "HQ sent me. Reception knows I\u2019m coming.",
    "Two minutes, I\u2019ve got three more sites.",
    "Just let me plug this in. It\u2019s read-only.",
  ];
  const flags = ["In a hurry", "Borrowed authority", "Wants your PC", "Unknown device"];
  return (
    <Frame label="Example: a helpful stranger at your desk" aria="Three things a visitor says to talk his way in, and four red flag chips: in a hurry, borrowed authority, wants your PC and unknown device." height={H}>
      <circle cx="28" cy="48" r="11" fill={C.amber} fillOpacity="0.14" />
      <text x="28" y="52" fontSize="10.5" fontWeight="700" fill={C.amber} textAnchor="middle">
        M
      </text>
      <text x="46" y="53" fontSize="14" fontWeight="700" fill={C.text}>
        The visitor
      </text>
      <rect x="284" y="38" width="60" height="20" rx="10" fill={C.panel2} stroke={C.line} />
      <text x="314" y="52" fontSize="10.5" fontWeight="700" fill={C.muted} textAnchor="middle">
        Pretext
      </text>
      <line x1="16" y1="70.5" x2="344" y2="70.5" stroke={C.line} />

      {lines.map((t, i) => {
        const y = 80 + i * 34;
        return (
          <g key={t}>
            <rect x="16.5" y={y + 0.5} width="327" height="28" rx="9" fill={C.panel2} stroke={C.line} />
            <text x="29" y={y + 18.5} fontSize="11.5" fill={C.text}>
              {t}
            </text>
            <circle className="sr-anim-ring" cx="328" cy={y + 14.5} r="4.5" fill="none" stroke={C.red} strokeWidth="1.5" />
            <circle cx="328" cy={y + 14.5} r="1.8" fill={C.red} />
          </g>
        );
      })}

      <text x="16" y="198" fontSize="10.5" fill={C.muted}>
        Red flags
      </text>
      {flags.map((f, i) => {
        const x = 16.5 + (i % 2) * 169;
        const y = 206 + Math.floor(i / 2) * 36;
        return (
          <g key={f}>
            <rect x={x} y={y} width="158" height="28" rx="14" fill={C.red} fillOpacity="0.1" stroke={C.red} strokeOpacity="0.45" />
            <path d={`M${x + 15} ${y + 8} v12`} stroke={C.red} strokeWidth="1.6" strokeLinecap="round" />
            <path d={`M${x + 15} ${y + 8.5} h8 l-2 3 l2 3 h-8`} fill={C.red} />
            <text x={x + 32} y={y + 18} fontSize="11.5" fontWeight="700" fill={C.red}>
              {f}
            </text>
          </g>
        );
      })}
      <text x="16" y="284" fontSize="10.5" fill={C.muted}>
        One flag is enough to stop and verify.
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 3. verify, sign in, escort: a four-step diagram plus lock reminder   */
/* ------------------------------------------------------------------ */
const STEPS: { title: string; sub: string }[] = [
  { title: "Keep the door shut", sub: "Do not buzz anyone in on a hunch" },
  { title: "Verify on a known number", sub: "Call HQ or reception, not a number he gives" },
  { title: "Sign in and badge", sub: "Visitor log, photo ID, visitor badge" },
  { title: "Escort, never alone", sub: "Stay with them; keep them off your PC" },
];

function EscortArt(): JSX.Element {
  const H = 296;
  return (
    <Frame label="The safe path for a visitor" aria="A four step path: keep the door shut, verify on a known number, sign in and badge, then escort. A reminder to lock your screen with Windows key plus L." height={H}>
      {STEPS.map((s, i) => {
        const y = 34 + i * 54;
        const active = i === 1;
        return (
          <g key={s.title}>
            {i > 0 ? <line x1="38" y1={y - 10} x2="38" y2={y} stroke={C.line} strokeWidth="1.5" /> : null}
            <rect x="16.5" y={y + 0.5} width="327" height="44" rx="10" fill={C.panel2} stroke={active ? C.mint : C.line} strokeOpacity={active ? 0.6 : 1} />
            <circle cx="38" cy={y + 22.5} r="12" fill={C.mint} fillOpacity={active ? 0.22 : 0.12} />
            {active ? <circle className="sr-anim-ring" cx="38" cy={y + 22.5} r="12" fill="none" stroke={C.mint} strokeWidth="1.5" /> : null}
            <text x="38" y={y + 27} fontSize="12" fontWeight="700" fill={C.mint} textAnchor="middle">
              {i + 1}
            </text>
            <text x="60" y={y + 20} fontSize="12.5" fontWeight="700" fill={C.text}>
              {s.title}
            </text>
            <text x="60" y={y + 35} fontSize="10.5" fill={C.muted}>
              {s.sub}
            </text>
          </g>
        );
      })}

      <rect x="16.5" y="254.5" width="327" height="30" rx="8" fill={C.amber} fillOpacity="0.1" stroke={C.amber} strokeOpacity="0.4" />
      <g fill="none" stroke={C.amber} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="27" y="266" width="12" height="9" rx="2" />
        <path d="M29.5 266 v-3 a3.5 3.5 0 0 1 7 0 v3" />
      </g>
      <circle className="sr-anim-ring" cx="33" cy="270.5" r="7" fill="none" stroke={C.amber} strokeWidth="1.2" />
      <text x="50" y="273.5" fontSize="12" fontWeight="600" fill={C.amber}>
        Leaving your desk? Lock it:
      </text>
      <rect x="236" y="262" width="64" height="17" rx="4" fill={C.code} stroke={C.line} />
      <text x="268" y="274" fontSize="10.5" fontFamily={MONO} fill={C.text} textAnchor="middle">
        Win + L
      </text>
      <rect className="sr-anim-blink" x="304" y="264" width="1.5" height="13" rx="0.75" fill={C.amber} />
    </Frame>
  );
}

export const SOCIAL_ENGINEERING_READING: ReadingSlide[] = [
  {
    tab: "Why it matters",
    headline: "The easiest way in is through the door",
    sections: [
      {
        h: "What this course is about",
        body: "You are alone in the office when a friendly visitor says he is from HQ IT. Nothing is hacked on screen. The only question is whether a stranger gets in and gets hands on your computer.",
      },
      {
        h: "Who it is for",
        body: "Anyone who works in an office, and especially front desk and reception. If you have a badge, a door code or a logged-in PC, this is for you.",
      },
      {
        h: "Why you are the control",
        body: "Locks, passwords and firewalls do nothing when a person opens the door and steps aside. A confident stranger is counting on you being polite. You can be kind and still check.",
      },
    ],
    illustration: EntranceArt,
    caption: "A lanyard and a laptop bag look official. They prove nothing.",
  },
  {
    tab: "What to watch for",
    headline: "Confident, helpful and in a hurry",
    sections: [
      {
        h: "Impersonation",
        body: "A company name, a lanyard and a tool bag feel official. Anyone can borrow them. Trust a check you make, not a story you hear.",
      },
      {
        h: "Tailgating",
        body: "Someone follows you through a door you just unlocked, or asks you to hold it. Politeness is the way in.",
      },
      {
        h: "Pressure",
        body: "\u201CTwo minutes.\u201D \u201CThe ticket is already open.\u201D \u201CReception knows I\u2019m coming.\u201D Urgency is there to make you skip the check.",
      },
      {
        h: "A device that should not be there",
        body: "Physical access lets someone plug in hardware, such as a keylogger. It records every keystroke and never needs your password.",
      },
    ],
    illustration: PretextArt,
    caption: "Each line sounds reasonable. Together they are a pattern.",
  },
  {
    tab: "What to do",
    headline: "Verify first, then escort",
    sections: [
      {
        h: "Verify",
        body: "Keep the door shut and call HQ or reception on a number you already know. Not one the visitor gives you.",
      },
      {
        h: "Sign in and escort",
        body: "A real visitor signs in, wears a badge and is never left alone. They never use your logged-in session.",
      },
      {
        h: "Lock and clear",
        body: "Press Win + L whenever you step away. Put confidential papers and removable media out of sight.",
      },
      {
        h: "Check and report",
        body: "Look at the back of your PC for adapters you do not recognise. If anyone plugged something in, tell IT straight away.",
      },
    ],
    illustration: EscortArt,
    caption: "Physical access is full access. Do not hand it to a stranger.",
  },
];
